import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useContext
} from "react";
import PropTypes from "prop-types";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box
} from "@material-ui/core";
import { Formik } from "formik";
import styled from "styled-components";
import moment from "moment";
import _ from "lodash";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import Dialog from "../../waybee-ui/Dialog/Dialog";
import Button from "../../waybee-ui/Button";
import SearchableSelect from "../../waybee-ui/SearchableSelect";
import TeacherService from "../../services/TeacherService";
import UserStore from "../../utils/UserStore";
import fetchGradeJobQueue from "../../utils/fetchGradeJobQueue";
import ConfirmAlert from "../../waybee-ui/ConfirmAlert";
import { AbilityContext } from "../../acl/can";
import {
  parseAdvancedConfig,
  isFieldHidden,
  isFieldRequired,
  canCreateOccurrence,
  joinOccurrenceNotes,
  findStageForDate,
  filterGroupsByIds
} from "../../utils/occurrenceAdvanced";
import RoundedAvatar from "../../waybee-ui/RoundedAvatar";
import OccurrenceTypeOption from "./OccurrenceTypeOption";
import OccurrenceNotesFields from "./OccurrenceNotesFields";
import OccurrenceAttachmentFields from "./OccurrenceAttachmentFields";
import OccurrenceAvailableWebField from "./OccurrenceAvailableWebField";
import {
  filterAllowedAttachments,
  notifyRejectedAttachment
} from "../../utils/occurrenceAttachmentRules";

const NO_DISCIPLINE_ID = "__none__";

const FormGrid = styled(Grid)`
  overflow: scroll;

  @media (min-height: 800px) {
    scrollbar-width: none;
  }
`;

const Content = styled(DialogContent)`
  overflow: hidden;
`;

const StudentName = styled(Typography)`
  font-size: 20px;
  color: ${({ theme }) => theme.color.secondary.dark};
`;

const StudentRa = styled(Typography)`
  font-size: 16px;
  opacity: 0.5;
  color: #000000;
`;

const LoadingContainer = styled.div`
  margin: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const HiddenContainer = styled.div`
  display: none;
`;

const groupHasReasons = group =>
  !!(group && Array.isArray(group.reasons) && group.reasons.length);

const resolveReason = values =>
  groupHasReasons(values.occurrenceType)
    ? values.reason
    : values.occurrenceType;

const formatOccurrenceDate = date => moment(date).format("YYYY-MM-DD HH:mm:ss");

const hasDisciplineLink = discipline =>
  !!(
    discipline &&
    discipline.id !== NO_DISCIPLINE_ID &&
    discipline.idTurmaDisc
  );

const studentSearchText = option =>
  [
    option && option.name,
    option && option.ra,
    option && option.rollCallNum,
    option && option.className,
    option && option.classId
  ]
    .map(value => String(value || "").toLowerCase())
    .join(" ");

const RestrictedOccurrenceTypeSync = ({
  restricted,
  selectableTypes,
  currentType,
  setFieldValue
}) => {
  useEffect(() => {
    if (!restricted) return;
    const currentId = currentType && currentType.id;
    if (selectableTypes.length === 1) {
      if (currentId !== selectableTypes[0].id) {
        setFieldValue("occurrenceType", selectableTypes[0]);
        setFieldValue("reason", "");
      }
      return;
    }
    if (currentId && !selectableTypes.some(type => type.id === currentId)) {
      setFieldValue("occurrenceType", "");
      setFieldValue("reason", "");
    }
  }, [restricted, selectableTypes, currentType, setFieldValue]);
  return null;
};

RestrictedOccurrenceTypeSync.propTypes = {
  restricted: PropTypes.bool.isRequired,
  selectableTypes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string }))
    .isRequired,
  currentType: PropTypes.oneOfType([
    PropTypes.shape({ id: PropTypes.string }),
    PropTypes.string
  ]),
  setFieldValue: PropTypes.func.isRequired
};

RestrictedOccurrenceTypeSync.defaultProps = {
  currentType: null
};

const formatStudentOption = (option, t) => {
  if (!option) return "";
  const bits = [];
  if (option.ra) {
    bits.push(`${t("occurrences.advanced.studentRa")} ${option.ra}`);
  }
  if (option.rollCallNum != null && option.rollCallNum !== "") {
    bits.push(`${t("occurrences.advanced.studentCall")} ${option.rollCallNum}`);
  }
  if (option.className || option.classId) {
    bits.push(
      `${t("occurrences.advanced.studentClass")} ${option.className ||
        option.classId}`
    );
  }
  return bits.length
    ? `${option.name || ""} — ${bits.join(" · ")}`
    : option.name || "";
};

const NewOccurrenceDialog = ({
  open,
  handleClose,
  studentId,
  studentName,
  students,
  rich,
  classId: fixedClassId,
  classes,
  allowedGroupIds
}) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const ability = useContext(AbilityContext);
  const pickStudent = Array.isArray(students);
  const pickClass = !fixedClassId && Array.isArray(classes);
  const isRich = !!rich || pickStudent;
  const canCreate = canCreateOccurrence(ability, isRich);
  const [classId, setClassId] = useState(fixedClassId);

  const noDisciplineOption = useMemo(
    () => ({
      id: NO_DISCIPLINE_ID,
      codDisc: null,
      name: t("occurrences.dialog.noDiscipline"),
      idPerlet: null,
      idTurmaDisc: null
    }),
    [t]
  );

  const [dateSelected, setDateSelected] = useState(null);
  const [hasClassAttached, setHasClassAttached] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [occurrenceTypes, setOccurrenceTypes] = useState([]);
  const [typesLoaded, setTypesLoaded] = useState(false);
  const [occurrenceStages, setOccurrenceStages] = useState([]);
  const [loadingOnSave, setLoadingOnSave] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [occurFiles, setOccurFiles] = useState([]);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [formConfig, setFormConfig] = useState(parseAdvancedConfig({}));
  const [selectedStudent, setSelectedStudent] = useState(null);
  const formatSelectedDate = schedule => {
    const [hour, minute] = schedule.startTime.split(":");
    return moment(schedule.date)
      .set({ hour, minute })
      .format("YYYY-MM-DD HH:mm:ss");
  };

  const resolveIdPerlet = (discipline, schedule) => {
    if (hasDisciplineLink(discipline) && discipline.idPerlet) {
      return discipline.idPerlet;
    }
    if (schedule && schedule.idPerLet) {
      return schedule.idPerLet;
    }
    if (schedules[0] && schedules[0].idPerLet) {
      return schedules[0].idPerLet;
    }
    return UserStore.getAnoLetivo() || moment(dateSelected || undefined).year();
  };

  const resolveDiscipline = disciplineId => {
    if (!disciplineId || disciplineId === NO_DISCIPLINE_ID) {
      return noDisciplineOption;
    }
    return (
      (disciplines || []).find(
        d =>
          String(d.id) === String(disciplineId) ||
          String(d.codDisc) === String(disciplineId)
      ) || noDisciplineOption
    );
  };

  const resolveSelectedStudentId = () =>
    (selectedStudent && (selectedStudent.ra || selectedStudent.id)) ||
    studentId ||
    "";

  const formatData = data => {
    const {
      disciplineId,
      discipline: disciplineFromForm,
      schedule,
      occurrenceType,
      obs,
      notes,
      internalNotes,
      occurrenceStage
    } = data;
    const reason = resolveReason(data);
    const discipline = disciplineFromForm || resolveDiscipline(disciplineId);
    const files = [];
    const dateSchedule =
      dateSelected ||
      (schedule && schedule.startTime ? formatSelectedDate(schedule) : null);
    const idType = reason.id.split("-")[0];
    const linked = hasDisciplineLink(discipline);

    occurFiles.map(occurFile =>
      files.push({
        name: occurFile.attachmentName,
        content: occurFile.attachmentContent
      })
    );

    const payload = {
      absences: [],
      idPerlet: resolveIdPerlet(discipline, schedule),
      date: dateSchedule,
      occurrences: [
        {
          description: joinOccurrenceNotes(notes || obs, internalNotes),
          type: reason.name,
          course: linked ? discipline.name : " - ",
          idGroup: occurrenceType.idGroup,
          idType,
          files,
          studentId: resolveSelectedStudentId(),
          notificationPermission: occurrenceType.notificationPermission,
          title: occurrenceType.name,
          availableWeb: false
        }
      ],
      removed: []
    };

    if (linked) {
      payload.idTurmaDisc = discipline.idTurmaDisc;
    } else if (schedule && schedule.idTurmaDisc) {
      payload.idTurmaDisc = schedule.idTurmaDisc;
    }

    if (occurrenceStage && occurrenceStage.id) {
      payload.occurrences[0].codEtapa = occurrenceStage.id;
    }

    return payload;
  };

  const filterTeacherDiscipline = schedule => {
    const filteredDisciplines = schedule.reduce(
      (uniqueDisciplines, currentItem) => {
        const isUnique = !uniqueDisciplines.some(
          item => item.codDisc === currentItem.codDisc
        );
        if (isUnique) {
          uniqueDisciplines.push(currentItem);
        }
        return uniqueDisciplines;
      },
      []
    );

    return filteredDisciplines.map(discipline => ({
      id: discipline.codDisc,
      codDisc: discipline.codDisc,
      name: discipline.name,
      idPerlet: discipline.idPerLet,
      idTurmaDisc: discipline.idTurmaDisc
    }));
  };

  const getOccurrenceTypes = async lesson => {
    const types = await TeacherService.getOccurrenceTypes(
      classId,
      lesson && lesson.idTurmaDisc,
      lesson && lesson.codDisc
    );

    setOccurrenceTypes(Array.isArray(types) ? types : []);
    setTypesLoaded(true);
  };

  const getOccurrencesStages = async data => {
    let date = moment();
    if (data && data.date) {
      date = moment(data.date);
    } else if (dateSelected) {
      date = moment(dateSelected);
    }
    const codPerlet = date.year();

    const stages = await TeacherService.getOccurrenceStages(
      classId,
      codPerlet,
      data && data.codDisc
    );

    const list = Array.isArray(stages) ? stages : [];
    const unique = [];
    const seen = new Set();
    list.forEach(stage => {
      const key = String(
        (stage && stage.name) || (stage && stage.id) || ""
      ).trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      unique.push(stage);
    });
    setOccurrenceStages(unique);
    return unique;
  };

  const getSchedules = useCallback(async () => {
    setDisciplines([]);
    setSchedules([]);
    setOccurrenceStages([]);
    setHasClassAttached(true);
    setTypesLoaded(false);
    if (!classId) {
      return;
    }

    setLoadingContent(true);

    try {
      const classSchedule = await TeacherService.getScheduleForOccurrence(
        classId
      );

      const list = Array.isArray(classSchedule) ? classSchedule : [];
      if (list.length > 0 && !list[0].idTurmaDisc) {
        setHasClassAttached(false);
      }

      const filteredDisciplines = filterTeacherDiscipline(list);

      setDisciplines(filteredDisciplines);
      setSchedules(list);

      await getOccurrenceTypes(null);
      await getOccurrencesStages({ codDisc: null, date: moment() });
    } catch (e) {
      console.error(e);
      setDisciplines([]);
      setSchedules([]);
      try {
        await getOccurrenceTypes(null);
      } catch (typesErr) {
        console.error(typesErr);
        setOccurrenceTypes([]);
      }
    } finally {
      setLoadingContent(false);
    }
  }, [classId]);

  const requestSave = values => {
    setPendingValues(values);
    setConfirmSaveOpen(true);
  };

  const closeConfirmSave = () => {
    setConfirmSaveOpen(false);
    setPendingValues(null);
  };

  const insertOccurrence = async data => {
    const filteredData = {
      ...data,
      discipline: resolveDiscipline(data.disciplineId)
    };

    if (!hasClassAttached) {
      filteredData.schedule = schedules;
    }

    if (
      !dateSelected &&
      !(filteredData.schedule && filteredData.schedule.startTime)
    ) {
      return;
    }

    const occurData = formatData(filteredData);
    setLoadingOnSave(true);
    closeConfirmSave();
    try {
      await TeacherService.saveOccurrence(occurData, {
        classId,
        filter: {
          course: { id: resolveSelectedStudentId() },
          period: "Individual"
        }
      });
      alert.show(t("common.messages.processingRequest"), {
        title: t("occurrences.dialog.sent")
      });
      fetchGradeJobQueue();
      handleClose(true);
    } catch (e) {
      console.error(e);
      const message = _.get(
        e,
        "response.data.error.message",
        t("occurrences.dialog.sendError")
      );
      alert.show(message, { title: t("common.actions.error") });
    } finally {
      setLoadingOnSave(false);
    }
  };

  const confirmSave = () => {
    if (!pendingValues) return;
    insertOccurrence(pendingValues);
  };

  const handleFileSelect = (e, setFieldValue) => {
    const files = filterAllowedAttachments(
      e.target.files,
      notifyRejectedAttachment(alert, t)
    );
    if (files.length > 0) {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const reader = new FileReader();

        reader.onload = event => {
          const fileData = {
            id: file.lastModified + file.size,
            name: file.name,
            content: event.target.result.split(",")[1]
          };

          occurFiles.push({
            attachmentName: fileData.name,
            attachmentContent: fileData.content
          });

          if (occurFiles) {
            setFieldValue("attachmentName", [...occurFiles, fileData.name]);
            setFieldValue("attachmentData", [...occurFiles, fileData.content]);
          } else {
            setFieldValue("attachmentName", [fileData.name]);
            setFieldValue("attachmentData", [fileData.content]);
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const deleteAttachment = occurFile => {
    const files = _.filter(
      occurFiles,
      file =>
        file.attachmentName !== occurFile.attachmentName &&
        file.attchmentContent !== occurFile.attachmentContent
    );
    setOccurFiles(files);
  };

  const applyStageForDate = (stages, date, setFieldValue) => {
    const stage = findStageForDate(stages, date);
    if (!stage || !setFieldValue) {
      return stage;
    }
    setFieldValue("occurrenceStageId", String(stage.id));
    setFieldValue("occurrenceStage", stage);
    return stage;
  };

  const setSelectedDiscipline = async (disciplineId, setFieldValue) => {
    setFieldValue("disciplineId", disciplineId);
    setFieldValue("occurrenceType", "");
    setFieldValue("reason", "");

    const discipline = resolveDiscipline(disciplineId);

    if (!hasDisciplineLink(discipline)) {
      await getOccurrenceTypes(null);
      await getOccurrencesStages({
        codDisc: null,
        date: moment(dateSelected || undefined)
      });
      return;
    }

    const filteredData = { ...schedules[0], ...discipline };
    await getOccurrenceTypes(filteredData);
    await getOccurrencesStages(filteredData);
  };

  useEffect(() => {
    if (!open) return;
    if (fixedClassId) {
      setClassId(fixedClassId);
    } else {
      const options = classes || [];
      setClassId(options.length === 1 ? String(options[0].id) : undefined);
    }
  }, [open, fixedClassId, classes]);

  useEffect(() => {
    if (open) {
      getSchedules();
    }
  }, [getSchedules, open]);

  useEffect(() => {
    if (open) {
      TeacherService.getOccurrenceAdvancedConfig()
        .then(config => setFormConfig(parseAdvancedConfig(config)))
        .catch(() => setFormConfig(parseAdvancedConfig({})));
      const fromList = (students || []).find(
        item =>
          String(item.id) === String(studentId) ||
          String(item.ra) === String(studentId)
      );
      if (fromList) {
        setSelectedStudent(fromList);
      } else if (studentId) {
        setSelectedStudent({
          id: studentId,
          name: studentName,
          ra: studentId
        });
      } else {
        setSelectedStudent(null);
      }
      setDateSelected(formatOccurrenceDate(new Date()));
    }
  }, [open, studentId, studentName, students]);

  const handleClassChange = (nextClassId, setFieldValue) => {
    setClassId(nextClassId);
    setFieldValue("schedule", "");
    setFieldValue("disciplineId", NO_DISCIPLINE_ID);
    setFieldValue("occurrenceType", "");
    setFieldValue("reason", "");
    setFieldValue("occurrenceStageId", "");
    setFieldValue("occurrenceStage", null);
  };

  const handleDateSelection = async (date, setFieldValue) => {
    setDateSelected(formatOccurrenceDate(date));
    if (!classId) return;
    const stages = occurrenceStages.length
      ? occurrenceStages
      : await getOccurrencesStages({
          date,
          codDisc: null
        });
    applyStageForDate(stages, date, setFieldValue);
  };

  const hideDiscipline = isFieldHidden(formConfig, "discipline");
  const hideStage = isFieldHidden(formConfig, "stage");
  const hideLesson = isFieldHidden(formConfig, "lesson");
  const hideAttachments = isFieldHidden(formConfig, "attachments");
  const hideObservations = isFieldHidden(formConfig, "observations");
  const requireDiscipline = isFieldRequired(formConfig, "discipline");
  const requireStage = isFieldRequired(formConfig, "stage");
  const requireLesson = isFieldRequired(formConfig, "lesson");
  const requireObservations = isFieldRequired(formConfig, "observations");
  const groupsRestricted = !!allowedGroupIds;
  const selectableTypes = useMemo(
    () =>
      allowedGroupIds
        ? filterGroupsByIds(occurrenceTypes, allowedGroupIds)
        : occurrenceTypes,
    [occurrenceTypes, allowedGroupIds]
  );
  const groupLocked = groupsRestricted && selectableTypes.length === 1;

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={isRich ? "md" : "sm"}
        closeButton
      >
        <DialogTitle>
          {isRich
            ? t("occurrences.advanced.newTitle")
            : t("occurrences.dialog.new")}
        </DialogTitle>
        <Formik
          initialValues={{
            disciplineId: NO_DISCIPLINE_ID,
            occurrenceStageId: "",
            notes: "",
            internalNotes: ""
          }}
        >
          {({ values, handleChange, setFieldValue }) => (
            <FormGrid>
              <RestrictedOccurrenceTypeSync
                restricted={groupsRestricted}
                selectableTypes={selectableTypes}
                currentType={values.occurrenceType}
                setFieldValue={setFieldValue}
              />
              <Content>
                {pickStudent ? (
                  <FormControl fullWidth margin="normal">
                    <SearchableSelect
                      label={t("occurrences.advanced.student")}
                      placeholder={t("occurrences.advanced.studentPlaceholder")}
                      options={students}
                      value={selectedStudent}
                      onChange={setSelectedStudent}
                      disablePortal={false}
                      getOptionLabel={option => formatStudentOption(option, t)}
                      filterOptions={(opts, state) => {
                        const q = String(
                          (state && state.inputValue) || ""
                        ).toLowerCase();
                        if (!q) return opts;
                        return (opts || []).filter(
                          option => studentSearchText(option).indexOf(q) >= 0
                        );
                      }}
                      renderOption={option => (
                        <Box display="flex" alignItems="center">
                          <RoundedAvatar
                            imageSrc={option.photo}
                            imageAlt={option.name}
                            size={36}
                          >
                            {option.name}
                          </RoundedAvatar>
                          <Box marginLeft="12px" minWidth={0}>
                            <Typography variant="body1" noWrap>
                              {option.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {[
                                option.ra
                                  ? `${t("occurrences.advanced.studentRa")} ${
                                      option.ra
                                    }`
                                  : "",
                                option.rollCallNum != null &&
                                option.rollCallNum !== ""
                                  ? `${t("occurrences.advanced.studentCall")} ${
                                      option.rollCallNum
                                    }`
                                  : "",
                                option.className || option.classId
                                  ? `${t(
                                      "occurrences.advanced.studentClass"
                                    )} ${option.className || option.classId}`
                                  : ""
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      getOptionSelected={(a, b) =>
                        String((a && (a.ra || a.id)) || "") ===
                        String((b && (b.ra || b.id)) || "")}
                      noOptionsText={t("students.empty")}
                      TextFieldProps={{
                        InputLabelProps: { shrink: true },
                        margin: "none",
                        required: true
                      }}
                    />
                  </FormControl>
                ) : (
                  <>
                    <StudentName>{studentName}</StudentName>
                    <StudentRa>
                      {t("occurrences.ra", { ra: studentId })}
                    </StudentRa>
                  </>
                )}
                <KeyboardDatePicker
                  fullWidth
                  disableToolbar
                  format="dd/MM/yyyy"
                  margin="normal"
                  id="date-picker-inline"
                  label={t("common.labels.date")}
                  value={dateSelected}
                  onChange={date => handleDateSelection(date, setFieldValue)}
                  placeholder="DD/MM/YYYY"
                />
                {pickClass && classes.length === 0 && (
                  <Typography color="error">
                    {t("occurrences.dialog.noClassAvailable")}
                  </Typography>
                )}
                {pickClass && classes.length > 0 && (
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel id="occurrence-class-label">
                      {t("common.labels.class")}
                    </InputLabel>
                    <Select
                      labelId="occurrence-class-label"
                      value={classId || ""}
                      onChange={e =>
                        handleClassChange(e.target.value, setFieldValue)}
                    >
                      {classes.map(item => (
                        <MenuItem key={item.id} value={String(item.id)}>
                          {item.name && item.name !== item.id
                            ? `${item.id} - ${item.name}`
                            : item.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Grid container>
                  <Grid container spacing={2}>
                    {loadingContent ? (
                      <LoadingContainer>
                        <CircularProgress />
                      </LoadingContainer>
                    ) : (
                      <Grid item xs={12}>
                        {!hideLesson && (
                          <FormControl
                            fullWidth
                            margin="normal"
                            required={requireLesson}
                          >
                            <InputLabel id="schedule-label">
                              {t("common.labels.lesson")}
                            </InputLabel>
                            <Select
                              id="schedule"
                              name="schedule"
                              labelId="schedule-label"
                              value={values.schedule ? values.schedule : ""}
                              fullWidth
                              onChange={e => {
                                handleChange(e);
                                getOccurrenceTypes(e.target.value);
                                getOccurrencesStages(e.target.value);
                              }}
                            >
                              {(schedules || []).map(lesson => (
                                <MenuItem key={lesson.id} value={lesson}>
                                  {lesson.name}
                                  {' '}
                                  -
                                  {" "}
                                  {moment(lesson.date).format("DD/MM")}
                                  {" "}
                                  {lesson.startTime}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        {hideLesson && (
                          <HiddenContainer>
                            <FormControl fullWidth margin="normal">
                              <InputLabel id="schedule-label">
                                {t("common.labels.lesson")}
                              </InputLabel>
                              <Select
                                id="schedule"
                                name="schedule"
                                labelId="schedule-label"
                                value={values.schedule ? values.schedule : ""}
                                fullWidth
                                onChange={e => {
                                  handleChange(e);
                                  getOccurrenceTypes(e.target.value);
                                  getOccurrencesStages(e.target.value);
                                }}
                              >
                                {(schedules || []).map(lesson => (
                                  <MenuItem key={lesson.id} value={lesson}>
                                    {lesson.name}
                                    {' '}
                                    -
                                    {" "}
                                    {moment(lesson.date).format("DD/MM")}
                                    {" "}
                                    {lesson.startTime}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </HiddenContainer>
                        )}
                        <Grid container spacing={2}>
                          {!hideDiscipline && (
                            <Grid item xs={6}>
                              <FormControl
                                fullWidth
                                margin="normal"
                                required={requireDiscipline}
                              >
                                <SearchableSelect
                                  label={t("common.labels.discipline")}
                                  options={[
                                    noDisciplineOption,
                                    ...(disciplines || [])
                                  ]}
                                  value={resolveDiscipline(
                                    values.disciplineId || NO_DISCIPLINE_ID
                                  )}
                                  onChange={discipline => {
                                    setSelectedDiscipline(
                                      discipline
                                        ? String(discipline.id)
                                        : NO_DISCIPLINE_ID,
                                      setFieldValue
                                    );
                                  }}
                                  disableClearable
                                  getOptionLabel={o => o.name || ""}
                                  getOptionSelected={(a, b) =>
                                    String(a.id) === String(b.id)}
                                  noOptionsText={t(
                                    "common.messages.noDiscipline"
                                  )}
                                  TextFieldProps={{
                                    InputLabelProps: { shrink: true },
                                    margin: "none",
                                    required: requireDiscipline
                                  }}
                                />
                              </FormControl>
                            </Grid>
                          )}
                          {!hideStage && (
                            <Grid item xs={6}>
                              <FormControl
                                fullWidth
                                margin="normal"
                                required={requireStage}
                              >
                                <InputLabel id="occurrenceStage-label" shrink>
                                  {requireStage
                                    ? t("occurrences.stageRequired")
                                    : t("occurrences.stageOptional")}
                                </InputLabel>
                                <Select
                                  id="occurrenceStageId"
                                  name="occurrenceStageId"
                                  labelId="occurrenceStage-label"
                                  value={
                                    values.occurrenceStageId != null
                                      ? String(values.occurrenceStageId)
                                      : ""
                                  }
                                  displayEmpty
                                  fullWidth
                                  renderValue={selected => {
                                    if (selected === "" || selected == null) {
                                      return occurrenceStages.length
                                        ? t("occurrences.noStage")
                                        : t("occurrences.advanced.stageEmpty");
                                    }
                                    const stage = (occurrenceStages || []).find(
                                      s => String(s.id) === String(selected)
                                    );
                                    return (
                                      (stage &&
                                        (stage.shortName
                                          ? `${stage.shortName} — ${stage.name}`
                                          : stage.name)) ||
                                      t("occurrences.noStage")
                                    );
                                  }}
                                  onChange={e => {
                                    const stageId = e.target.value;
                                    setFieldValue("occurrenceStageId", stageId);
                                    const stage = (occurrenceStages || []).find(
                                      s => String(s.id) === String(stageId)
                                    );
                                    setFieldValue(
                                      "occurrenceStage",
                                      stage || ""
                                    );
                                  }}
                                >
                                  <MenuItem value="">
                                    {occurrenceStages.length
                                      ? t("occurrences.noStage")
                                      : t("occurrences.advanced.stageEmpty")}
                                  </MenuItem>
                                  {(occurrenceStages || []).map(
                                    occurrenceStage => (
                                      <MenuItem
                                        key={`${occurrenceStage.id}-${occurrenceStage.name}`}
                                        value={String(occurrenceStage.id)}
                                      >
                                        {occurrenceStage.shortName
                                          ? `${occurrenceStage.shortName} — ${occurrenceStage.name}`
                                          : occurrenceStage.name}
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                        </Grid>
                        {groupsRestricted &&
                          typesLoaded &&
                          !selectableTypes.length && (
                            <Typography color="error">
                              {t("occurrences.dialog.menuGroupsUnavailable")}
                            </Typography>
                          )}
                        <Grid container spacing={2}>
                          {!groupLocked && (
                            <Grid
                              item
                              xs={
                                groupHasReasons(values.occurrenceType) ? 6 : 12
                              }
                            >
                              <FormControl fullWidth margin="normal">
                                <InputLabel id="occurrenceType-label">
                                  {t("occurrences.student.groups")}
                                </InputLabel>
                                <Select
                                  id="occurrenceType"
                                  name="occurrenceType"
                                  labelId="occurrenceType-label"
                                  value={values.occurrenceType || ""}
                                  onChange={handleChange}
                                  fullWidth
                                  renderValue={selected => {
                                    if (!selected) return "";
                                    return (
                                      <OccurrenceTypeOption
                                        occurrence={selected}
                                      />
                                    );
                                  }}
                                >
                                  {selectableTypes.map(occurrenceType => (
                                    <MenuItem
                                      key={occurrenceType.id}
                                      value={occurrenceType}
                                    >
                                      <OccurrenceTypeOption
                                        occurrence={occurrenceType}
                                      />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                          {groupHasReasons(values.occurrenceType) && (
                            <Grid item xs={groupLocked ? 12 : 6}>
                              <FormControl fullWidth margin="normal">
                                <InputLabel id="reason-label">
                                  {t("occurrences.student.types")}
                                </InputLabel>
                                <Select
                                  fullWidth
                                  name="reason"
                                  id="reason"
                                  labelId="reason-label"
                                  value={values.reason || ""}
                                  onChange={handleChange}
                                  renderValue={selected => {
                                    if (!selected) return "";
                                    return (
                                      <OccurrenceTypeOption
                                        occurrence={{
                                          ...selected,
                                          nivel:
                                            selected.nivel ||
                                            (values.occurrenceType &&
                                              values.occurrenceType.nivel)
                                        }}
                                      />
                                    );
                                  }}
                                >
                                  {(values.occurrenceType.reasons || []).map(
                                    reason => (
                                      <MenuItem key={reason.id} value={reason}>
                                        <OccurrenceTypeOption
                                          occurrence={{
                                            ...reason,
                                            nivel:
                                              reason.nivel ||
                                              values.occurrenceType.nivel
                                          }}
                                        />
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                        </Grid>
                        {resolveReason(values) && (
                          <>
                            <OccurrenceNotesFields
                              notesName="notes"
                              notesValue={values.notes}
                              onChange={handleChange}
                              hideNotes={hideObservations}
                              requireNotes={requireObservations}
                              margin="normal"
                            />
                            {!hideAttachments && (
                              <OccurrenceAttachmentFields
                                files={occurFiles}
                                canAdd
                                spacedButton
                                onSelect={e =>
                                  handleFileSelect(e, setFieldValue)}
                                onDelete={deleteAttachment}
                              />
                            )}
                            <OccurrenceAvailableWebField
                              margin="normal"
                              checked={values.availableWeb}
                            />
                          </>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Content>
              <DialogActions>
                {loadingOnSave ? (
                  <CircularProgress />
                ) : (
                  canCreate && (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={
                        !classId ||
                        !resolveSelectedStudentId() ||
                        !dateSelected ||
                        !resolveReason(values) ||
                        (requireDiscipline &&
                          !hasDisciplineLink(
                            resolveDiscipline(values.disciplineId)
                          )) ||
                        (requireStage &&
                          occurrenceStages.length > 0 &&
                          !values.occurrenceStageId) ||
                        (requireLesson &&
                          !(values.schedule && values.schedule.startTime)) ||
                        (requireObservations &&
                          !String(values.notes || values.obs || "").trim())
                      }
                      onClick={() => requestSave(values)}
                    >
                      {t("common.actions.save")}
                    </Button>
                  )
                )}
              </DialogActions>
            </FormGrid>
          )}
        </Formik>
      </Dialog>
      <ConfirmAlert
        open={confirmSaveOpen}
        onClose={closeConfirmSave}
        onConfirm={confirmSave}
        title={t("occurrences.dialog.saveConfirmTitle")}
        text={t("occurrences.dialog.saveConfirm")}
        confirmButtonText={t("common.actions.save")}
        confirmButtonColor="primary"
        cancelButtonText={t("common.actions.cancel")}
      />
    </MuiPickersUtilsProvider>
  );
};

NewOccurrenceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  studentId: PropTypes.string,
  studentName: PropTypes.string,
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      photo: PropTypes.string,
      ra: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      rollCallNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      className: PropTypes.string,
      classId: PropTypes.string
    })
  ),
  rich: PropTypes.bool,
  classId: PropTypes.string,
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string
    })
  ),
  allowedGroupIds: PropTypes.arrayOf(PropTypes.string)
};

NewOccurrenceDialog.defaultProps = {
  studentId: "",
  studentName: "",
  students: undefined,
  rich: false,
  classId: undefined,
  classes: undefined,
  allowedGroupIds: null
};

export default NewOccurrenceDialog;
